import { motion } from "framer-motion";
import { Download } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import CarbonImpactDashboard from "../components/CarbonImpactDashboard";
import CertificationBadgeGrid from "../components/CertificationBadgeGrid";
import WelfareInitiativeCard from "../components/WelfareInitiativeCard";
import FishingBanStatement from "../components/FishingBanStatement";
import EcoPackagingImpact from "../components/EcoPackagingImpact";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePdfDownload from "../hooks/usePdfDownload";
import { WELFARE_INITIATIVES } from "../data/welfareInitiatives";

export default function Sustainability() {
  useDocumentTitle("Sustainability & Community Impact");
  const { status: pdfStatus, download: handleDownloadReport } = usePdfDownload(
    () => import("../components/SustainabilityReportPdf"),
    "ZenFresh-Sustainability-Report.pdf",
    "sustainability_report_download"
  );

  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[12%] bottom-10 hidden sm:block" size={36} delay={0.5} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Sustainability &amp; Community Impact
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Ocean and Coast, Both Looked After
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Our carbon footprint, our certifications, and the harbour communities who supply us — the
            state of all three, honestly reported.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-6"
          >
            <button
              type="button"
              onClick={handleDownloadReport}
              disabled={pdfStatus === "generating"}
              className="inline-flex items-center gap-2 rounded-full bg-teal-400 px-6 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
            >
              <Download className="h-4 w-4" />
              {pdfStatus === "generating" ? "Preparing PDF…" : "Download Impact Report"}
            </button>
            {pdfStatus === "error" && (
              <p className="mt-2 text-xs text-coral-300">Couldn&apos;t generate the PDF — please try again.</p>
            )}
          </motion.div>
        </div>
      </section>

      <Breadcrumbs />

      <section id="carbon-impact" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Carbon Impact</h2>
          <p className="mt-4 text-ocean-800/70">
            Aggregated, anonymised metrics — no individual order or customer data.
          </p>
        </ScrollReveal>
        <div className="mt-12">
          <CarbonImpactDashboard />
        </div>
      </section>

      <section id="certifications" className="scroll-mt-24 bg-foam-100 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Certifications</h2>
          </ScrollReveal>
          <div className="mt-12">
            <CertificationBadgeGrid />
          </div>
        </div>
      </section>

      <section id="community" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">
            Community &amp; Harbour Welfare
          </h2>
          <p className="mt-4 text-ocean-800/70">
            The people at the start of our supply chain, and what we do to support them.
          </p>
        </ScrollReveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {WELFARE_INITIATIVES.map((initiative, i) => (
            <ScrollReveal key={initiative.id} delay={i * 0.08}>
              <WelfareInitiativeCard initiative={initiative} />
            </ScrollReveal>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-ocean-800/40">
          Harbour photography courtesy of Wikimedia Commons contributors (CC BY-SA / CC0). Hover a photo for
          credit.
        </p>
      </section>

      <section id="fishing-ban-transparency" className="scroll-mt-24 bg-foam-100 py-24">
        <div className="mx-auto max-w-3xl px-6">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">
              Seasonal Fishing Ban Transparency
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.08} className="mt-10">
            <FishingBanStatement />
          </ScrollReveal>
        </div>
      </section>

      <section id="eco-packaging" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">
            Eco-Packaging &amp; Low-Carbon Ordering
          </h2>
        </ScrollReveal>
        <div className="mt-12">
          <EcoPackagingImpact />
        </div>
      </section>
    </PageTransition>
  );
}
